import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

const FIELDS = [
  ["specialty", "Specialty"],
  ["city", "City"],
  ["phone", "Phone"],
];

function Snapshot({ title, s, testid }) {
  return (
    <div className="flex-1 min-w-[240px] rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs" data-testid={testid}>
      <div className="font-semibold text-slate-800 mb-1">{title}</div>
      <pre className="whitespace-pre-wrap break-all text-slate-700">{JSON.stringify(
        { name: s.name, email: s.email, credential_status: s.credential_status, rejection_reason: s.rejection_reason, profile: s.profile }, null, 2)}</pre>
    </div>
  );
}

function RepairForm({ c, onDone }) {
  const [form, setForm] = useState({ professional_type: "none", specialty: "", city: "", phone: "", credential_status: "pending", rejection_reason: "" });
  const [preview, setPreview] = useState(null);
  const done = preview && !preview.dry_run;
  const set = (k) => (v) => { setForm(f => ({ ...f, [k]: v })); setPreview(null); };
  const body = (dry) => ({
    user_id: c.user_id, dry_run: dry, credential_status: form.credential_status,
    rejection_reason: form.credential_status === "rejected" ? form.rejection_reason || null : null,
    restore_profile: {
      professional_type: form.professional_type === "none" ? null : form.professional_type,
      specialty: form.specialty || null, city: form.city || null, phone: form.phone || null,
    },
  });
  const run = async (dry) => {
    try {
      const { data } = await api.post("/admin/repair/split-whatsapp-signup", body(dry));
      setPreview(data);
      if (!dry) { toast.success(`Split done · ${data.documents_moved} documents moved`); onDone?.(); }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Repair failed");
    }
  };
  return (
    <Card className="p-5 rounded-2xl border-amber-300 bg-amber-50/40" data-testid={`split-card-${c.user_id}`}>
      <div className="font-semibold">{c.name} · {c.email}</div>
      <div className="text-xs text-slate-600 mt-1">
        A WhatsApp sign-up ({c.whatsapp_documents} documents) is attached to this admin account. Enter the account's original details; empty fields are removed.
      </div>
      <div className="grid sm:grid-cols-3 gap-3 mt-4">
        <div>
          <Label className="text-xs">Doctor / nurse type</Label>
          <Select value={form.professional_type} onValueChange={set("professional_type")}>
            <SelectTrigger data-testid="split-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="doctor">Doctor</SelectItem>
              <SelectItem value="nurse">Nurse</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {FIELDS.map(([k, l]) => (
          <div key={k}>
            <Label className="text-xs">{l}</Label>
            <Input value={form[k]} onChange={e => set(k)(e.target.value)} data-testid={`split-${k}`} />
          </div>
        ))}
        <div>
          <Label className="text-xs">Verification status</Label>
          <Select value={form.credential_status} onValueChange={set("credential_status")}>
            <SelectTrigger data-testid="split-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="verified">Verified</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {form.credential_status === "rejected" && (
          <div>
            <Label className="text-xs">Rejection reason</Label>
            <Input value={form.rejection_reason} onChange={e => set("rejection_reason")(e.target.value)} data-testid="split-reason" />
          </div>
        )}
      </div>
      <div className="flex gap-2 mt-4">
        <Button size="sm" variant="outline" className="rounded-full" disabled={done} onClick={() => run(true)} data-testid="split-preview">Preview before / after</Button>
        <Button size="sm" disabled={!preview || !preview.dry_run} className="rounded-full bg-emerald-700 hover:bg-emerald-800" onClick={() => run(false)} data-testid="split-confirm">
          Confirm split
        </Button>
      </div>
      {preview && (
        <div className="mt-4" data-testid="split-result">
          <div className="text-xs font-semibold text-slate-700 mb-2">
            {preview.dry_run ? `Preview: ${preview.documents_to_move.length} documents would move` : `Done: ${preview.documents_moved} documents moved`}
          </div>
          <div className="flex flex-wrap gap-3">
            <Snapshot title="Admin account before" s={preview.admin_before} testid="split-before" />
            <Snapshot title="Admin account after" s={preview.admin_after} testid="split-after" />
            <Snapshot title={`New WhatsApp account ${preview.new_account.user_id}`} s={preview.new_account} testid="split-new" />
          </div>
        </div>
      )}
    </Card>
  );
}

export function SplitSignupRepair({ onDone }) {
  const [items, setItems] = useState([]);
  const load = async () => {
    try { setItems((await api.get("/admin/repair/split-candidates")).data); } catch { /* admin-only; ignore */ }
  };
  useEffect(() => { load(); }, []);
  if (!items.length) return null;
  return (
    <section data-testid="split-section">
      <h3 className="font-display text-lg font-semibold mb-3">WhatsApp sign-up attached to an admin account</h3>
      <div className="space-y-4">{items.map(c => <RepairForm key={c.user_id} c={c} onDone={() => { onDone?.(); }} />)}</div>
    </section>
  );
}
